import { Component, Input, OnInit } from '@angular/core';
// ...existing code...
import { validateMediaFile } from '../../media/media-validation';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Model } from '../../models/model';
import { ModelService } from '../model.service';
import { AuthService } from '../../auth/auth.service';
import { ImageService } from '../../media/media.firebase.service';
import { UserService } from '../../auth/user.service';
import { User } from '../../models/user';
import { Router } from '@angular/router';
import { calculateAge, minAgeValidator, cleanSocialHandle, buildSocialUrl } from '../../shared/helpers';

@Component({
  selector: 'app-model-detail-form',
  templateUrl: './model-detail-form.component.html',
  styleUrls: ['./model-detail-form.component.css']
})
export class ModelDetailFormComponent implements OnInit {
  loading = false;
  dataLoading = true;
  isUserModel = false; // Track if this user is allowed to be a model
  @Input() model: Model | null = null;
  @Input() userEmail: string | null = null;
  @Input() allowModelCreation = false; // Allow new model creation during registration
  modelForm: FormGroup;
  selectedFile: File | null = null;
  selectedFileName: string = '';
  selectedBannerFile: File | null = null;
  selectedBannerFileName: string = '';

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private modelService: ModelService,
    private authService: AuthService,
    private imageService: ImageService,
    private userService: UserService,
    private router: Router
  ) {
    this.modelForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.maxLength(300)],
      date_of_birth: ['', [Validators.required, minAgeValidator(18)]],
      gender: ['', Validators.required],
      phone_number: [''],
      email: ['', [Validators.required, Validators.email]],
      profile_image_url: [''],
      profile_banner_url: [''],
      twitter_username: [''],
      instagram_username: [''],
      city: [''],
      province: [''],
      country: [''],
      tags: [''],
    });
  }

  ngOnInit(): void {
    const userId = this.authService.getCurrentUid();
    if (!this.model && userId) {
      this.userService.getUserById(userId).subscribe(user => {
        if (user && user.has_profile && user.is_model) {
          this.isUserModel = true;
          this.modelService.getModelById(userId).subscribe(model => {
            this.model = model;
            this.patchModelForm(model);
            this.modelForm.get('email')?.disable();
            this.dataLoading = false;
          }, () => { this.dataLoading = false; });
        } else {
          this.dataLoading = false;
        }
      }, () => { this.dataLoading = false; });
    } else if (this.model) {
      this.patchModelForm(this.model);
      this.modelForm.get('email')?.disable();
      this.dataLoading = false;
    } else {
      this.dataLoading = false;
    }
    if (this.userEmail) {
      this.modelForm.patchValue({ email: this.userEmail });
      this.modelForm.get('email')?.disable();
    }
  }

  /** Patch form with model data, handling date properly */
  private patchModelForm(model: Model): void {
    let twitter_username = '';
    let instagram_username = '';
    if (model.twitter_url) {
      const twMatch = model.twitter_url.match(/https:\/\/x\.com\/(.+)/);
      twitter_username = twMatch ? twMatch[1] : '';
    }
    if (model.instagram_url) {
      const igMatch = model.instagram_url.match(/https:\/\/www\.instagram\.com\/(.+)/);
      instagram_username = igMatch ? igMatch[1] : '';
    }

    // Only use date_of_birth if it's a valid date and not the epoch fallback
    const isValidDob = model.date_of_birth
      && !isNaN(model.date_of_birth.getTime())
      && model.date_of_birth.getTime() > 0;

    this.modelForm.patchValue({
      name: model.name,
      description: model.description,
      date_of_birth: isValidDob ? model.date_of_birth : null,
      gender: model.gender,
      phone_number: model.phone_number,
      email: model.email,
      profile_image_url: model.profile_image_url,
      profile_banner_url: model.profile_banner_url || '',
      twitter_username,
      instagram_username,
      city: model.city,
      province: model.province,
      country: model.country,
      tags: model.tags
    });
  }


  onFileSelected(event: Event, type: 'profile' | 'banner' = 'profile'): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validationResult = validateMediaFile(file, 'image');
      if (!validationResult.valid) {
        this.snackBar.open(validationResult.error || 'Invalid file.', 'Close', { duration: 4000 });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (type === 'profile') {
          this.selectedFile = file;
          this.selectedFileName = file.name;
          this.modelForm.patchValue({ profile_image_url: reader.result as string });
        } else {
          this.selectedBannerFile = file;
          this.selectedBannerFileName = file.name;
          this.modelForm.patchValue({ profile_banner_url: reader.result as string });
        }
        this.snackBar.open('File uploaded successfully', 'Close', {
          duration: 3000,
        });
      };
      reader.readAsDataURL(file);
    }
  }


  triggerFileInput(type: 'profile' | 'banner' = 'profile'): void {
    const fileInput = document.getElementById(type === 'profile' ? 'profile-image' : 'profile-banner') as HTMLInputElement;
    fileInput.click();
  }

  async onSubmit(): Promise<void> {
    if (this.modelForm.valid) {
      // Guard: Only allow model users or explicit new model creation
      const userId = this.authService.getCurrentUid() || '';
      if (!this.isUserModel && !this.allowModelCreation) {
        this.snackBar.open('Only model accounts can update model profiles', 'Close', { duration: 4000 });
        return;
      }
      this.loading = true;
      const submitModel = async () => {
        // Get all form values, including disabled fields
        const formValues = this.modelForm.getRawValue();
        // Clean social handles & build URLs
        const cleanTwitter = cleanSocialHandle(formValues.twitter_username);
        const cleanInstagram = cleanSocialHandle(formValues.instagram_username);
        const twitter_url = buildSocialUrl('twitter', cleanTwitter);
        const instagram_url = buildSocialUrl('instagram', cleanInstagram);
        // Get current user UID from AuthService
        const userId = this.authService.getCurrentUid() || '';
        // Build the payload
        const rawDob = formValues.date_of_birth;
        let dob: Date;
        if (rawDob instanceof Date) {
          dob = rawDob;
        } else {
          dob = new Date(rawDob);
        }
        if (isNaN(dob.getTime())) {
          this.snackBar.open('Invalid date of birth. Please enter a valid date.', 'Close', { duration: 3000 });
          this.loading = false;
          return;
        }
        let updatedModel = new Model({
          model_id: userId,
          user_id: userId,
          name: formValues.name,
          description: formValues.description,
          date_of_birth: dob,
          age: calculateAge(dob).toString(),
          gender: formValues.gender,
          phone_number: formValues.phone_number,
          email: formValues.email,
          profile_image_url: formValues.profile_image_url,
          profile_banner_url: formValues.profile_banner_url,
          twitter_url,
          instagram_url,
          city: formValues.city,
          province: formValues.province,
          country: formValues.country,
          tags: formValues.tags
        });
        // If a file is selected, upload to Firebase and set profile_image_url
        if (this.selectedFile) {
          try {
            const imageUrl = await this.imageService.uploadImage(
              this.selectedFile,
              `model_images/${Date.now()}_${this.selectedFile.name}`
            );
            if (imageUrl) updatedModel.profile_image_url = imageUrl;
          } catch (error) {
            this.snackBar.open('Image upload failed', 'Close', { duration: 3000 });
            return;
          }
        }
        // If a banner file is selected, upload to Firebase and set profile_banner_url
        if (this.selectedBannerFile) {
          try {
            const bannerUrl = await this.imageService.uploadImage(
              this.selectedBannerFile,
              `model_banners/${Date.now()}_${this.selectedBannerFile.name}`
            );
            if (bannerUrl) updatedModel.profile_banner_url = bannerUrl;
          } catch (error) {
            this.snackBar.open('Banner upload failed', 'Close', { duration: 3000 });
            return;
          }
        }
        const updateUserProfile = () => {
          this.userService.getUserById(userId).subscribe((user) => {
            if (!user) return;
            const updatedUser = new User({
              ...user,
              has_profile: true,
              is_model: true,
              user_id: user.user_id || ''
            });
            this.userService.updateUser(updatedUser).subscribe();
          });
        };
        
        // Sets model onboarding state to complete (for new model creation)
        const setOnboardingComplete = () => {
          this.userService.setModelOnboardingComplete(userId).subscribe();
        };
        
        if (this.model && this.model.model_id) {
          // Update existing model
          this.modelService.updateModel(updatedModel).subscribe({
            next: (response) => {
              this.snackBar.open('Model updated successfully', 'Close', {
                duration: 3000,
              });
              updateUserProfile();
              this.loading = false;
            },
            error: (error) => {
              this.snackBar.open('Failed to update model', 'Close', {
                duration: 3000,
              });
              this.loading = false;
            }
          });
        } else {
          // Create new model
          this.modelService.createModel(updatedModel).subscribe({
            next: (response) => {
              this.snackBar.open('Model created successfully', 'Close', {
                duration: 3000,
              });
              updateUserProfile();
              setOnboardingComplete(); // Set onboarding complete on new model creation
              this.router.navigate(['/models']);
              this.loading = false;
            },
            error: (error) => {
              this.snackBar.open('Failed to create model', 'Close', {
                duration: 3000,
              });
              this.loading = false;
            }
          });
        }
      };
      submitModel();
    } else {
      this.loading = false;
    }
  }

  onClose(): void {
    this.router.navigate(['../']); // Go back one route; adjust as needed for your routing structure
  }
}

// minAgeValidator now imported from shared/helpers